/**
 * Catering Page - Bulaccino Cafe
 * Premium corporate catering portal with:
 * - Mobile-first responsive design
 * - Browse menu with category navigation
 * - Build catering request
 * - Account management
 * - Order tracking
 * - M-PAiSA payment integration
 * 
 * Based on BG Titan Bulaccino Catering OS Master Prompt
 */
import { useState, useEffect, useMemo } from 'react';
import { 
  Coffee, Users, Calendar, MapPin, Phone, Mail, Clock, ChevronRight, ChevronDown,
  Download, ShoppingBag, History, LogOut, User, Building, Building2, FileText, Package,
  Plus, Minus, Check, AlertCircle, Loader2, Eye, EyeOff, ArrowLeft, ArrowRight,
  CreditCard, Banknote, Smartphone, X, Utensils, Cake, Salad, Sandwich, 
  Flame, PieChart, GlassWater, CirclePlus, Star, Leaf, Wheat, Info, Trash2,
  Upload, Image as ImageIcon, QrCode, Copy, Bookmark, BookmarkPlus, Wallet
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import api from '../utils/apiClient';

// Business Info
const BUSINESS_INFO = {
  name: 'Bulaccino Cafe',
  phone: '+679 675 0109',
  email: 'bulaccinocafe@gmail.com',
  address: 'Shop 2 & 3 Concave Drive, Namaka - Nadi, Fiji',
  website: 'www.BulaccinoCafe.com',
  hours: 'Open 7 days - 7:00 AM to 6:00 PM',
  tagline: 'Corporate Catering for Meetings, Events & Celebrations',
  vatRate: 12.5,
  prepHandlingPercent: 30, // 30% Prep & Handling charge
  deliveryFee: 10, // $10 delivery fee
  depositPercent: 30,
  leadTimeHours: 48,
  minOrderValue: 100
};

// Category icons mapping
const CATEGORY_ICONS = {
  sweets: Cake,
  hot_snacks: Flame,
  quiches: PieChart,
  salads: Salad,
  sandwiches: Sandwich,
  wraps: Utensils,
  deli_pies: PieChart,
  cakes: Cake,
  beverages: GlassWater,
  extras: CirclePlus
};

// Occasion types for events
const OCCASIONS = [
  { value: 'meeting', label: 'Office Meeting', icon: Users },
  { value: 'training', label: 'Training / Workshop', icon: FileText },
  { value: 'birthday', label: 'Birthday Celebration', icon: Cake },
  { value: 'promotion', label: 'Promotion / Achievement', icon: Star },
  { value: 'farewell', label: 'Farewell Party', icon: Users },
  { value: 'corporate_event', label: 'Corporate Event', icon: Building },
  { value: 'wedding', label: 'Wedding / Engagement', icon: Star },
  { value: 'other', label: 'Other Occasion', icon: Calendar }
];

// Dietary tag colors
const DIETARY_COLORS = {
  vegetarian: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  vegan: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'gluten-free': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  halal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
};

export default function CateringPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // View state - persist to localStorage so refresh keeps user on same page
  const [view, setView] = useState(() => {
    const savedView = localStorage.getItem('catering_view');
    // Only restore certain views, not checkout or login flows
    if (savedView && ['landing', 'menu', 'cart', 'dashboard'].includes(savedView)) {
      return savedView;
    }
    return 'landing';
  }); // landing, menu, cart, checkout, login, register, forgot-password, dashboard
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  
  // Menu state
  const [menuData, setMenuData] = useState({ items: [], categories: {}, category_order: [], category_names: {} });
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Cart state
  const [cart, setCart] = useState([]);
  
  // Order form state
  const [orderForm, setOrderForm] = useState({
    occasion: '',
    occasion_other: '',
    event_date: '',
    event_time: '',
    guest_count: '',
    company_name: '',
    department: '',
    delivery_address: '',
    delivery_instructions: '',
    needs_delivery: true,
    needs_setup: false,
    needs_staffing: false,
    contact_name: '',
    contact_phone: '',
    dietary_requirements: '',
    special_requests: '',
    purchase_order: '',
    payment_method: 'mpaisa'
  });
  
  // Auth form state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    email: '', password: '', confirmPassword: '', full_name: '', 
    phone: '', company_name: '', company_address: '', position: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Orders state (for dashboard)
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('orders'); // orders, templates, profile
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    default_guest_count: '',
    default_occasion: '',
    notes: ''
  });
  
  // M-PAiSA payment proof state
  const [showPaymentModal, setShowPaymentModal] = useState(null); // holds order_id
  const [mpaisaInfo, setMpaisaInfo] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('mpaisa'); // mpaisa, bank, cash
  const [paymentProofForm, setPaymentProofForm] = useState({
    transaction_ref: '',
    payer_phone: '',
    amount: '',
    payment_date: '',
    notes: ''
  });
  const [proofFile, setProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  
  // New order payment state (for immediate payment after order submission)
  const [newOrderPayment, setNewOrderPayment] = useState(null); // { order_id, deposit_amount }
  
  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('catering_token');
    const savedClient = localStorage.getItem('catering_client');
    const savedView = localStorage.getItem('catering_view');
    
    if (token && savedClient) {
      try {
        const clientData = JSON.parse(savedClient);
        setClient(clientData);
        // If logged in and was on dashboard, restore dashboard view
        if (savedView === 'dashboard') {
          setView('dashboard');
        }
      } catch (e) {
        localStorage.removeItem('catering_token');
        localStorage.removeItem('catering_client');
      }
    }
    
    // Load saved cart
    const savedCart = localStorage.getItem('catering_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        localStorage.removeItem('catering_cart');
      }
    }
    
    // Fetch menu
    fetchMenu();
  }, []);
  
  // Save cart to localStorage
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem('catering_cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('catering_cart');
    }
  }, [cart]);
  
  // Save view state to localStorage so refresh keeps user on same page
  useEffect(() => {
    // Only persist certain views that make sense to restore
    if (['landing', 'menu', 'cart', 'dashboard'].includes(view)) {
      localStorage.setItem('catering_view', view);
    } else {
      // For login/register/checkout, don't persist - go to landing on refresh
      localStorage.setItem('catering_view', 'landing');
    }
  }, [view]);
  
  // Fetch menu data
  const fetchMenu = async () => {
    try {
      const res = await api.get('/api/catering/menu');
      
      // FORCE min_qty=1 for Meals + Salads items by NAME (hardcoded fix)
      const mealsSaladsPatterns = ['salad', 'fish fingers', 'chicken nibbles', 'chicken goujon', 'samosa', 'spring roll', 'pumpkin', 'feta'];
      const fixedData = { ...res.data };
      
      const shouldBeMinQty1 = (name) => {
        const lowerName = (name || '').toLowerCase();
        return mealsSaladsPatterns.some(p => lowerName.includes(p));
      };
      
      if (fixedData.items) {
        fixedData.items = fixedData.items.map(item => 
          (item.category === 'meals_salads' || shouldBeMinQty1(item.name))
            ? { ...item, min_qty: 1, min_quantity: 1 }
            : item
        );
      }
      // Fix in categories object too
      Object.keys(fixedData.categories || {}).forEach(catKey => {
        if (catKey.toLowerCase().includes('meal') || catKey.toLowerCase().includes('salad')) {
          fixedData.categories[catKey] = (fixedData.categories[catKey] || []).map(item => 
            ({ ...item, min_qty: 1, min_quantity: 1 })
          );
        } else {
          fixedData.categories[catKey] = (fixedData.categories[catKey] || []).map(item => 
            shouldBeMinQty1(item.name) ? { ...item, min_qty: 1, min_quantity: 1 } : item
          );
        }
      });
      
      setMenuData(fixedData);
      if (fixedData.category_order?.length > 0) {
        setActiveCategory(fixedData.category_order[0]);
      }
    } catch (err) {
      console.error('Failed to fetch menu:', err);
      toast.error('Failed to load menu');
    }
  };
  
  // Fetch client orders
  const fetchOrders = async () => {
    if (!client) return;
    try {
      const token = localStorage.getItem('catering_token');
      const res = await api.get('/api/catering/orders', {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: client.contact_id }
      });
      // Ensure orders is always an array
      const orderData = res.data.orders;
      setOrders(Array.isArray(orderData) ? orderData : []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      setOrders([]); // Reset to empty array on error
    }
  };
  
  // Fetch client templates
  const fetchTemplates = async () => {
    if (!client) return;
    setLoadingTemplates(true);
    try {
      const res = await api.get('/api/catering/templates', {
        params: { client_id: client.contact_id }
      });
      // Ensure templates is always an array
      const templateData = res.data.templates;
      setTemplates(Array.isArray(templateData) ? templateData : []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setTemplates([]); // Reset to empty array on error
    }
    setLoadingTemplates(false);
  };
  
  // Save cart as template
  const saveCartAsTemplate = async () => {
    if (!client) {
      toast.error('Please sign in to save templates');
      return;
    }
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (!templateForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setLoading(true);
    try {
      const templateData = {
        client_id: client.contact_id,
        name: templateForm.name.trim(),
        description: templateForm.description.trim(),
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit
        })),
        default_guest_count: parseInt(templateForm.default_guest_count) || null,
        default_occasion: templateForm.default_occasion || null,
        notes: templateForm.notes.trim()
      };
      
      await api.post('/api/catering/templates', templateData, {
        params: { client_id: client.contact_id }
      });
      toast.success(`Template "${templateForm.name}" saved successfully!`);
      setShowSaveTemplateModal(false);
      setTemplateForm({ name: '', description: '', default_guest_count: '', default_occasion: '', notes: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
    setLoading(false);
  };
  
  // Load template into cart
  const loadTemplateToCart = async (template) => {
    try {
      const res = await api.post(`/api/catering/templates/${template.template_id}/use`, null, {
        params: { client_id: client.contact_id }
      });
      
      // Convert template items to cart format
      const templateItems = res.data.items.map(item => ({
        item_id: item.item_id,
        name: item.name,
        unit_price: item.unit_price,
        unit: item.unit || 'each',
        quantity: item.quantity,
        min_qty: item.min_qty || 1,
        dietary_tags: item.dietary_tags || []
      }));
      
      setCart(templateItems);
      
      // Pre-fill order form if defaults exist
      if (res.data.default_guest_count) {
        setOrderForm(prev => ({ ...prev, guest_count: res.data.default_guest_count }));
      }
      if (res.data.default_occasion) {
        setOrderForm(prev => ({ ...prev, occasion: res.data.default_occasion }));
      }
      
      toast.success(`Loaded "${res.data.template_name}" - ${templateItems.length} items added to cart`);
      setView('cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load template');
    }
  };
  
  // Delete template
  const deleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Delete template "${templateName}"?`)) return;
    
    try {
      await api.delete(`/api/catering/templates/${templateId}`, {
        params: { client_id: client.contact_id }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };
  
  // Save order as template
  const saveOrderAsTemplate = async (order) => {
    const name = prompt('Enter template name:', `${order.occasion || 'Order'} - ${order.event_date}`);
    if (!name) return;
    
    try {
      await api.post(`/api/catering/templates/from-order/${order.order_id}`, null, {
        params: { client_id: client.contact_id, name }
      });
      toast.success(`Template "${name}" created from order`);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create template');
    }
  };
  
  // Fetch M-PAiSA merchant info
  const fetchMpaisaInfo = async () => {
    try {
      const res = await api.get('/api/catering/mpaisa-info');
      setMpaisaInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch M-PAiSA info:', err);
    }
  };
  
  // Open payment modal
  const openPaymentModal = (order) => {
    setShowPaymentModal(order);
    setPaymentMethod('mpaisa'); // Reset to default
    setPaymentProofForm({
      transaction_ref: '',
      payer_phone: '',
      amount: order.deposit_required?.toFixed(2) || '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setProofFile(null);
    fetchMpaisaInfo();
  };
  
  // Upload payment proof
  const uploadPaymentProof = async () => {
    if (!proofFile) {
      toast.error('Please select a screenshot of your payment');
      return;
    }
    if (!paymentProofForm.transaction_ref || !paymentProofForm.payer_phone || !paymentProofForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', proofFile);
      formData.append('transaction_ref', paymentProofForm.transaction_ref);
      formData.append('payer_phone', paymentProofForm.payer_phone);
      formData.append('amount', paymentProofForm.amount);
      formData.append('payment_date', paymentProofForm.payment_date);
      formData.append('notes', paymentProofForm.notes);
      
      await api.post(`/api/catering/orders/${showPaymentModal.order_id}/payment-proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Payment proof submitted! We will verify and confirm within 24 hours.');
      setShowPaymentModal(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload payment proof');
    }
    setUploadingProof(false);
  };
  
  // Auth functions
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/api/catering/login', loginForm);
      localStorage.setItem('catering_token', res.data.token);
      localStorage.setItem('catering_client', JSON.stringify(res.data.client));
      setClient(res.data.client);
      setView('dashboard');
      toast.success('Welcome back!');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/catering/register', registerForm);
      localStorage.setItem('catering_token', res.data.token);
      localStorage.setItem('catering_client', JSON.stringify(res.data.client));
      setClient(res.data.client);
      setView('dashboard');
      toast.success('Account created successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/api/catering/forgot-password', { email: forgotEmail });
      toast.success('Password reset link sent to your email');
      setView('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    }
    setLoading(false);
  };
  
  const handleLogout = () => {
    localStorage.removeItem('catering_token');
    localStorage.removeItem('catering_client');
    setClient(null);
    setView('landing');
    toast.success('Logged out successfully');
  };
  
  // Cart functions
  const addToCart = (item, quantity = 1) => {
    // Create a unique key for items with special instructions
    const itemKey = item.special_instruction 
      ? `${item.item_id}_${item.special_instruction}`
      : item.item_id;
    
    const existingIndex = cart.findIndex(c => {
      const cartKey = c.special_instruction 
        ? `${c.item_id}_${c.special_instruction}`
        : c.item_id;
      return cartKey === itemKey;
    });
    
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += quantity;
      setCart(newCart);
    } else {
      setCart([...cart, {
        item_id: item.item_id,
        name: item.name,
        unit_price: item.price,
        unit: item.unit,
        quantity: Math.max(item.min_qty || 1, quantity),
        min_qty: item.min_qty || 1,
        dietary_tags: item.dietary_tags || [],
        special_instruction: item.special_instruction || null
      }]);
    }
    
    const instructionText = item.special_instruction ? ` (${item.special_instruction})` : '';
    toast.success(`${item.name}${instructionText} added to cart`);
  };
  
  const updateCartQuantity = (itemId, newQty, specialInstruction = null) => {
    const item = cart.find(c => 
      c.item_id === itemId && c.special_instruction === specialInstruction
    );
    if (!item) return;
    
    const minQty = item.min_qty || 1;
    if (newQty < minQty) {
      removeFromCart(itemId, specialInstruction);
      return;
    }
    
    setCart(cart.map(c => 
      (c.item_id === itemId && c.special_instruction === specialInstruction) 
        ? { ...c, quantity: newQty } 
        : c
    ));
  };
  
  const removeFromCart = (itemId, specialInstruction = null) => {
    setCart(cart.filter(c => 
      !(c.item_id === itemId && c.special_instruction === specialInstruction)
    ));
    toast.success('Item removed from cart');
  };
  
  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('catering_cart');
  };
  
  // Calculate totals
  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const prepHandlingFee = subtotal * (BUSINESS_INFO.prepHandlingPercent / 100);
    const deliveryFee = orderForm.needs_delivery ? BUSINESS_INFO.deliveryFee : 0;
    const grossTotal = subtotal + prepHandlingFee + deliveryFee;
    const vatComponent = grossTotal * (BUSINESS_INFO.vatRate / (100 + BUSINESS_INFO.vatRate));
    const netExVat = grossTotal - vatComponent;
    const depositDue = grossTotal * (BUSINESS_INFO.depositPercent / 100);
    const balanceDue = grossTotal - depositDue;
    
    return {
      subtotal,
      prepHandlingFee,
      deliveryFee,
      grossTotal,
      vatIncluded: vatComponent,
      netExVat,
      depositDue,
      balanceDue,
      itemCount: cart.reduce((sum, item) => sum + item.quantity, 0)
    };
  }, [cart, orderForm.needs_delivery]);
  
  // Filter menu items
  const filteredItems = useMemo(() => {
    if (!activeCategory || !menuData.categories[activeCategory]) return [];
    
    let items = menuData.categories[activeCategory];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
    
    return items;
  }, [activeCategory, menuData.categories, searchQuery]);
  
  // Submit order
  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    if (!orderForm.event_date || !orderForm.event_time || !orderForm.guest_count) {
      toast.error('Please fill in all required event details');
      return;
    }
    
    if (!client) {
      toast.error('Please sign in to submit your request');
      setView('login');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('catering_token');
      const orderData = {
        ...orderForm,
        items: cart.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit,
          special_instruction: item.special_instruction || null
        })),
        subtotal: cartTotals.subtotal,
        prep_handling_fee: cartTotals.prepHandlingFee,
        delivery_fee: cartTotals.deliveryFee,
        gross_total: cartTotals.grossTotal,
        vat_included: cartTotals.vatIncluded,
        deposit_due: cartTotals.depositDue
      };
      
      const response = await api.post('/api/catering/orders', orderData, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: client.contact_id }
      });
      
      // Store the new order info for payment
      const newOrder = response.data;
      setNewOrderPayment({
        order_id: newOrder.order_id || newOrder.id,
        deposit_amount: cartTotals.depositDue,
        gross_total: cartTotals.grossTotal
      });
      
      // Reset payment form for the new order
      setPaymentProofForm({
        transaction_ref: '',
        payer_phone: client?.phone || '',
        amount: cartTotals.depositDue.toFixed(2),
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      toast.success('Order submitted! Please complete your 30% deposit payment.');
      clearCart();
      setOrderForm({
        occasion: '', occasion_other: '', event_date: '', event_time: '',
        guest_count: '', company_name: '', department: '', delivery_address: '',
        delivery_instructions: '', needs_delivery: true, needs_setup: false,
        needs_staffing: false, contact_name: '', contact_phone: '',
        dietary_requirements: '', special_requests: '', purchase_order: '',
        payment_method: 'mpaisa'
      });
      // Go to payment view instead of dashboard
      setView('payment');
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || 'Failed to submit order');
      } else if (errorMsg?.msg) {
        toast.error(errorMsg.msg);
      } else {
        toast.error('Failed to submit order. Please try again.');
      }
    }
    setLoading(false);
  };
  
  // ========== RENDER COMPONENTS ==========
  
  // Landing Page
  const renderLanding = () => (
    <div className="min-h-screen" data-testid="catering-landing">
      {/* Top Header with Logo, Contact Info, and Auth Links */}
      <div className="bg-white dark:bg-zinc-900 border-b border-border shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-lg p-1">
                <img 
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_bulaccino-live/artifacts/f6sx3sa6_bulaccino-cafe-logo-final-4-20-2026.png" 
                  alt="Bulaccino Cafe"
                  className="h-14 w-auto"
                />
              </div>
              <div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white">{BUSINESS_INFO.name}</h2>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">Corporate Catering</p>
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <a href={`tel:${BUSINESS_INFO.phone}`} className="flex items-center gap-1.5 text-zinc-700 dark:text-zinc-300 hover:text-amber-600">
                <Phone className="w-4 h-4" />
                <span>{BUSINESS_INFO.phone}</span>
              </a>
              <div className="hidden sm:flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400">
                <MapPin className="w-4 h-4" />
                <span>{BUSINESS_INFO.address}</span>
              </div>
            </div>
            
            {/* Auth Links */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-500 dark:text-zinc-400 hidden sm:inline">Already have an account?</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setView('login')} 
                className="rounded-full"
                data-testid="sign-in-btn"
              >
                Sign In
              </Button>
              <Button 
                size="sm"
                onClick={() => setView('register')} 
                className="rounded-full bg-amber-600 hover:bg-amber-700"
                data-testid="create-account-btn"
              >
                Create Account
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-amber-600 via-orange-500 to-amber-700 text-white">
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Corporate Catering
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-6">
              {BUSINESS_INFO.tagline}
            </p>
            <p className="text-lg text-white/80 mb-8">
              From morning teas to full corporate events, we deliver quality food 
              that makes your occasion memorable.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => setView('menu')}
                className="bg-white text-amber-700 hover:bg-amber-50 rounded-full px-8"
                data-testid="browse-menu-btn"
              >
                <ShoppingBag className="w-5 h-5 mr-2" />
                Browse Menu & Order
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8"
                onClick={async () => {
                  try {
                    toast.info('Generating PDF menu...');
                    const response = await api.get('/api/catering/menu/download/pdf', {
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'Bulaccino_Catering_Menu.pdf');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('PDF downloaded!');
                  } catch (err) {
                    toast.error('Failed to download menu');
                  }
                }}
              >
                <Download className="w-5 h-5 mr-2" />
                Download PDF Menu
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* How It Works - Simple Steps */}
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">How It Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 - Browse Menu FIRST */}
            <div className="bg-card rounded-2xl border border-border p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center">1</div>
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4 mt-2">
                <ShoppingBag className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Browse Our Menu</h3>
              <p className="text-sm text-muted-foreground">
                Explore our sweets, sandwiches, salads, platters & more
              </p>
            </div>
            
            {/* Step 2 - Create Account */}
            <div className="bg-card rounded-2xl border border-border p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center">2</div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4 mt-2">
                <User className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Create an Account</h3>
              <p className="text-sm text-muted-foreground">
                Sign up with your company details to place orders
              </p>
            </div>
            
            {/* Step 3 - Place Order */}
            <div className="bg-card rounded-2xl border border-border p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center">3</div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4 mt-2">
                <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">Place Your Order</h3>
              <p className="text-sm text-muted-foreground">
                Select date, time, delivery details & submit your order
              </p>
            </div>
            
            {/* Step 4 - We Deliver */}
            <div className="bg-card rounded-2xl border border-border p-6 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center">4</div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4 mt-2">
                <Check className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">We Deliver!</h3>
              <p className="text-sm text-muted-foreground">
                Confirm with 30% deposit & we'll handle the rest
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Info Cards */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">Lead Time</h3>
            <p className="text-muted-foreground">
              Please allow at least <strong>48 hours</strong> notice for catering orders. 
              Same-day requests subject to availability.
            </p>
          </div>
          
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <Banknote className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">Payment Terms</h3>
            <p className="text-muted-foreground">
              <strong>30% deposit</strong> required to confirm your order. 
              Balance due on delivery. Corporate accounts available.
            </p>
          </div>
          
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">Delivery</h3>
            <p className="text-muted-foreground">
              Free pickup from our cafe. Delivery available within Nadi area. 
              Setup and staffing services on request.
            </p>
          </div>
        </div>
      </div>
      
      {/* Contact Info & Google Maps */}
      <div className="bg-muted/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Visit Us</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Details */}
            <div className="space-y-6">
              <div className="bg-card rounded-2xl border border-border p-6">
                <h3 className="font-semibold text-lg text-foreground mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <a href={`tel:${BUSINESS_INFO.phone}`} className="flex items-center gap-3 text-foreground hover:text-amber-600 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{BUSINESS_INFO.phone}</p>
                      <p className="text-sm text-muted-foreground">Call us to place an order</p>
                    </div>
                  </a>
                  
                  <a href={`mailto:${BUSINESS_INFO.email}`} className="flex items-center gap-3 text-foreground hover:text-amber-600 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{BUSINESS_INFO.email}</p>
                      <p className="text-sm text-muted-foreground">Email us your requirements</p>
                    </div>
                  </a>
                  
                  <div className="flex items-center gap-3 text-foreground">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{BUSINESS_INFO.address}</p>
                      <p className="text-sm text-muted-foreground">Our cafe location</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 text-foreground">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{BUSINESS_INFO.hours}</p>
                      <p className="text-sm text-muted-foreground">Trading hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Google Maps Embed */}
            <div className="bg-card rounded-2xl border border-border overflow-hidden h-[350px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3908.8397799657584!2d177.44377!3d-17.7606!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6e17e86c7a7c8b9d%3A0x8d7e5a1b2c3d4e5f!2sBulaccino%20Cafe!5e0!3m2!1sen!2sfj!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bulaccino Cafe Location"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA Section - Encourage browsing menu first */}
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold text-foreground mb-4">See What's On The Menu</h2>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
          Browse our full catering menu to find the perfect selection for your event. No account needed to explore!
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button 
            size="lg"
            onClick={() => setView('menu')}
            className="rounded-full bg-amber-600 hover:bg-amber-700 px-8"
            data-testid="browse-menu-cta"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Browse Catering Menu
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-4">
          Already know what you want? <button onClick={() => setView('register')} className="text-amber-600 hover:underline font-medium">Create an account</button> to place your order.
        </p>
      </div>
    </div>
  );
  
  // Menu Browser
  const renderMenu = () => (
    <div className="min-h-screen bg-background" data-testid="catering-menu">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setView('landing')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            
            <h1 className="text-xl font-bold text-foreground">Catering Menu</h1>
            
            <button 
              onClick={() => setView('cart')}
              className="relative flex items-center gap-2 text-foreground"
              data-testid="view-cart-btn"
            >
              <ShoppingBag className="w-6 h-6" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {cartTotals.itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Category Tabs */}
      <div className="sticky top-[73px] z-30 bg-background border-b border-border overflow-x-auto">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-2 py-3">
            {menuData.category_order.map(catId => {
              const Icon = CATEGORY_ICONS[catId] || Package;
              const isActive = activeCategory === catId;
              return (
                <button
                  key={catId}
                  onClick={() => setActiveCategory(catId)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                    isActive 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  data-testid={`category-tab-${catId}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {menuData.category_names[catId] || catId}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Menu Items */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map(item => (
            <MenuItemCard 
              key={item.item_id} 
              item={item} 
              onAddToCart={addToCart}
              cartItem={cart.find(c => c.item_id === item.item_id)}
            />
          ))}
        </div>
        
        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No items in this category</p>
          </div>
        )}
      </div>
      
      {/* Floating Cart Summary */}
      {cart.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto">
          <button
            onClick={() => setView('cart')}
            className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-2xl p-4 flex items-center justify-between shadow-lg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold">{cartTotals.itemCount} items</div>
                <div className="text-sm text-white/80">View cart</div>
              </div>
            </div>
            <div className="text-xl font-bold">
              ${cartTotals.grossTotal.toFixed(2)}
            </div>
          </button>
        </div>
      )}
    </div>
  );
  
  // Cart View
  const renderCart = () => (
    <div className="min-h-screen bg-background" data-testid="catering-cart">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setView('menu')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Menu</span>
            </button>
            <h1 className="text-xl font-bold text-foreground">Your Cart</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {cart.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-6">Start adding items from our menu</p>
            <Button onClick={() => setView('menu')} className="rounded-full">
              Browse Menu
            </Button>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="space-y-3 mb-6">
              {cart.map((item, index) => {
                // Create unique key for items with special instructions
                const itemKey = item.special_instruction 
                  ? `${item.item_id}_${item.special_instruction}_${index}`
                  : `${item.item_id}_${index}`;
                
                return (
                <div key={itemKey} className="bg-card rounded-xl border border-border p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{item.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        ${item.unit_price.toFixed(2)} / {item.unit}
                      </p>
                      {item.special_instruction && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded inline-block">
                          {item.special_instruction}
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.item_id, item.special_instruction)}
                      className="text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateCartQuantity(item.item_id, item.quantity - 1, item.special_instruction)}
                        className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-medium text-foreground">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateCartQuantity(item.item_id, item.quantity + 1, item.special_instruction)}
                        className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="font-semibold text-foreground">
                      ${(item.unit_price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                  
                  {item.min_qty > 1 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Minimum order: {item.min_qty}
                    </p>
                  )}
                </div>
              );
              })}
            </div>
            
            {/* Order Summary */}
            <div className="bg-card rounded-2xl border border-border p-6 mb-6">
              <h3 className="font-semibold text-foreground mb-4">Order Summary</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground">${cartTotals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prep & Handling (30%)</span>
                  <span className="text-foreground">${cartTotals.prepHandlingFee.toFixed(2)}</span>
                </div>
                {cartTotals.deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery Fee</span>
                    <span className="text-foreground">${cartTotals.deliveryFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-border my-2 pt-2">
                  <div className="flex justify-between font-semibold">
                    <span className="text-foreground">Total (VAT Inclusive)</span>
                    <span className="text-foreground">${cartTotals.grossTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>VAT ({BUSINESS_INFO.vatRate}%) included</span>
                    <span>${cartTotals.vatIncluded.toFixed(2)}</span>
                  </div>
                </div>
                <div className="border-t border-border my-2 pt-2">
                  <div className="flex justify-between text-amber-600 dark:text-amber-400">
                    <span>Deposit Due (30%)</span>
                    <span className="font-semibold">${cartTotals.depositDue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Balance on Delivery</span>
                    <span>${cartTotals.balanceDue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Save as Template button */}
            {client && cart.length > 0 && (
              <Button 
                variant="outline"
                onClick={() => setShowSaveTemplateModal(true)}
                className="w-full rounded-full mb-4"
              >
                <BookmarkPlus className="w-5 h-5 mr-2" />
                Save Cart as Template
              </Button>
            )}
            
            {/* Proceed Button */}
            <Button 
              onClick={() => setView('checkout')}
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              size="lg"
            >
              Continue to Event Details
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
  
  // Checkout / Event Details
  const renderCheckout = () => (
    <div className="min-h-screen bg-background" data-testid="catering-checkout">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setView('cart')}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Cart</span>
            </button>
            <h1 className="text-xl font-bold text-foreground">Event Details</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmitOrder(); }} className="space-y-6">
          {/* Occasion */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              Event Details
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Occasion *
                </label>
                <select
                  value={orderForm.occasion}
                  onChange={(e) => setOrderForm({...orderForm, occasion: e.target.value})}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
                  required
                >
                  <option value="">Select occasion</option>
                  {OCCASIONS.map(occ => (
                    <option key={occ.value} value={occ.value}>{occ.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Number of Guests *
                </label>
                <Input
                  type="number"
                  min="1"
                  value={orderForm.guest_count}
                  onChange={(e) => setOrderForm({...orderForm, guest_count: e.target.value})}
                  placeholder="Expected headcount"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Event Date *
                </label>
                <Input
                  type="date"
                  value={orderForm.event_date}
                  onChange={(e) => setOrderForm({...orderForm, event_date: e.target.value})}
                  min={new Date(Date.now() + 48*60*60*1000).toISOString().split('T')[0]}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Event Time *
                </label>
                <Input
                  type="time"
                  value={orderForm.event_time}
                  onChange={(e) => setOrderForm({...orderForm, event_time: e.target.value})}
                  required
                />
              </div>
            </div>
          </div>
          
          {/* Company / Delivery */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-500" />
              Company & Delivery
            </h3>
            
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Company / Organization
                  </label>
                  <Input
                    value={orderForm.company_name}
                    onChange={(e) => setOrderForm({...orderForm, company_name: e.target.value})}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Department
                  </label>
                  <Input
                    value={orderForm.department}
                    onChange={(e) => setOrderForm({...orderForm, department: e.target.value})}
                    placeholder="Department (optional)"
                  />
                </div>
              </div>
              
              <div>
                <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={orderForm.needs_delivery}
                    onChange={(e) => setOrderForm({...orderForm, needs_delivery: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <div>
                    <div className="font-medium text-foreground">Delivery Required</div>
                    <div className="text-xs text-muted-foreground">Uncheck for cafe pickup</div>
                  </div>
                </label>
              </div>
              
              {orderForm.needs_delivery && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Delivery Address *
                    </label>
                    <Textarea
                      value={orderForm.delivery_address}
                      onChange={(e) => setOrderForm({...orderForm, delivery_address: e.target.value})}
                      placeholder="Full delivery address"
                      rows={2}
                      required={orderForm.needs_delivery}
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderForm.needs_setup}
                        onChange={(e) => setOrderForm({...orderForm, needs_setup: e.target.checked})}
                        className="w-5 h-5 rounded"
                      />
                      <div>
                        <div className="font-medium text-foreground">Buffet Setup</div>
                        <div className="text-xs text-muted-foreground">+$75</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={orderForm.needs_staffing}
                        onChange={(e) => setOrderForm({...orderForm, needs_staffing: e.target.checked})}
                        className="w-5 h-5 rounded"
                      />
                      <div>
                        <div className="font-medium text-foreground">Staffing Service</div>
                        <div className="text-xs text-muted-foreground">Quote provided</div>
                      </div>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Contact */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-amber-500" />
              Event Day Contact
            </h3>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contact Name
                </label>
                <Input
                  value={orderForm.contact_name}
                  onChange={(e) => setOrderForm({...orderForm, contact_name: e.target.value})}
                  placeholder="Person to contact on event day"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Contact Phone
                </label>
                <Input
                  value={orderForm.contact_phone}
                  onChange={(e) => setOrderForm({...orderForm, contact_phone: e.target.value})}
                  placeholder="+679 xxx xxxx"
                />
              </div>
            </div>
          </div>
          
          {/* Special Requests */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              Additional Information
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Dietary Requirements / Allergies
                </label>
                <Textarea
                  value={orderForm.dietary_requirements}
                  onChange={(e) => setOrderForm({...orderForm, dietary_requirements: e.target.value})}
                  placeholder="Any dietary restrictions or allergies we should know about"
                  rows={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Special Requests
                </label>
                <Textarea
                  value={orderForm.special_requests}
                  onChange={(e) => setOrderForm({...orderForm, special_requests: e.target.value})}
                  placeholder="Any special requests or instructions"
                  rows={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Purchase Order Number (if applicable)
                </label>
                <Input
                  value={orderForm.purchase_order}
                  onChange={(e) => setOrderForm({...orderForm, purchase_order: e.target.value})}
                  placeholder="PO number for invoicing"
                />
              </div>
            </div>
          </div>
          
          {/* Terms Notice */}
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-2xl border border-amber-200 dark:border-amber-800 p-6">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-2">Before you submit:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>This submits a catering request (not a confirmed order)</li>
                  <li>We will review availability and contact you to confirm</li>
                  <li>30% deposit required to confirm your order</li>
                  <li>Cancellations within 24 hours may forfeit deposit</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Submit */}
          {!client ? (
            <div className="text-center">
              <p className="text-muted-foreground mb-4">Please sign in to submit your request</p>
              <div className="flex justify-center gap-4">
                <Button type="button" onClick={() => setView('login')} className="rounded-full">
                  Sign In
                </Button>
                <Button type="button" variant="outline" onClick={() => setView('register')} className="rounded-full">
                  Create Account
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              type="submit"
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Catering Request
                  <Check className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
  
  // Payment View - After order submission
  const renderPayment = () => {
    const handleSubmitPaymentProof = async () => {
      if (!paymentProofForm.transaction_ref || !paymentProofForm.payer_phone) {
        toast.error('Please enter your M-PAiSA transaction reference and phone number');
        return;
      }
      
      setUploadingProof(true);
      try {
        const token = localStorage.getItem('catering_token');
        await api.post(`/api/catering/orders/${newOrderPayment.order_id}/payment-proof`, {
          transaction_ref: paymentProofForm.transaction_ref,
          payer_phone: paymentProofForm.payer_phone,
          amount: paymentProofForm.amount,
          payment_date: paymentProofForm.payment_date,
          notes: paymentProofForm.notes,
          payment_method: 'mpaisa'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Payment details submitted! We will verify and confirm your order within 24 hours.');
        setNewOrderPayment(null);
        fetchOrders();
        setView('dashboard');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to submit payment details');
      }
      setUploadingProof(false);
    };

    return (
      <div className="min-h-screen bg-background" data-testid="catering-payment">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="w-20" />
              <h1 className="text-xl font-bold text-foreground">Complete Your Deposit</h1>
              <div className="w-20" />
            </div>
          </div>
        </div>
        
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Success Message */}
          <div className="bg-green-50 dark:bg-green-950/30 rounded-2xl border border-green-200 dark:border-green-800 p-6 text-center">
            <Check className="w-12 h-12 text-green-600 dark:text-green-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-green-700 dark:text-green-300 mb-2">Order Submitted!</h2>
            <p className="text-green-600 dark:text-green-400">
              Please pay your 30% deposit to confirm your order.
            </p>
          </div>
          
          {/* Deposit Amount */}
          <div className="bg-card rounded-2xl border border-border p-6 text-center">
            <p className="text-muted-foreground mb-2">30% Deposit Required</p>
            <p className="text-4xl font-bold text-amber-600 dark:text-amber-400">
              ${newOrderPayment?.deposit_amount?.toFixed(2) || '0.00'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Total Order: ${newOrderPayment?.gross_total?.toFixed(2) || '0.00'}
            </p>
          </div>
          
          {/* Payment Method Selection */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4 text-center">Select Payment Method</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentMethod('mpaisa')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === 'mpaisa' 
                    ? 'border-red-500 bg-red-50 dark:bg-red-950/30' 
                    : 'border-border hover:border-red-300'
                }`}
              >
                <QrCode className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="font-medium text-foreground">M-PAiSA</p>
                <p className="text-xs text-muted-foreground">Pay instantly</p>
              </button>
              <button
                onClick={() => setPaymentMethod('cash')}
                className={`p-4 rounded-xl border-2 transition-all ${
                  paymentMethod === 'cash' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                    : 'border-border hover:border-green-300'
                }`}
              >
                <Banknote className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-foreground">Cash</p>
                <p className="text-xs text-muted-foreground">Pay at cafe</p>
              </button>
            </div>
          </div>
          
          {/* M-PAiSA Payment Section */}
          {paymentMethod === 'mpaisa' && (
            <div className="bg-red-50 dark:bg-red-950/30 rounded-2xl border border-red-200 dark:border-red-800 p-6">
              <h3 className="font-semibold text-red-700 dark:text-red-300 mb-4 text-center">
                Scan QR Code to Pay with M-PAiSA
              </h3>
              
              {/* Large QR Code */}
              <div className="flex justify-center mb-6">
                <img 
                  src="/mpaisa-qr-code.png" 
                  alt="M-PAiSA QR Code" 
                  className="w-64 h-64 border-4 border-red-300 dark:border-red-700 rounded-xl shadow-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=mpaisa://pay/9290013';
                  }}
                />
              </div>
              
              <p className="text-center text-red-600 dark:text-red-400 mb-6">
                Pay <strong>${newOrderPayment?.deposit_amount?.toFixed(2)}</strong> to Bulaccino Cafe
              </p>
              
              {/* Transaction Details Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                    M-PAiSA Transaction Reference *
                  </label>
                  <Input
                    type="text"
                    value={paymentProofForm.transaction_ref}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, transaction_ref: e.target.value})}
                    placeholder="Enter your M-PAiSA confirmation code"
                    className="bg-white dark:bg-gray-900"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                    Your M-PAiSA Phone Number *
                  </label>
                  <Input
                    type="tel"
                    value={paymentProofForm.payer_phone}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, payer_phone: e.target.value})}
                    placeholder="e.g., 9991234"
                    className="bg-white dark:bg-gray-900"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                    Amount Paid
                  </label>
                  <Input
                    type="text"
                    value={paymentProofForm.amount}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, amount: e.target.value})}
                    placeholder="Amount you paid"
                    className="bg-white dark:bg-gray-900"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                    Notes (optional)
                  </label>
                  <textarea
                    value={paymentProofForm.notes}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, notes: e.target.value})}
                    placeholder="Any additional details"
                    className="w-full rounded-lg border border-input bg-white dark:bg-gray-900 px-3 py-2 text-foreground"
                    rows={2}
                  />
                </div>
                
                <Button
                  onClick={handleSubmitPaymentProof}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  size="lg"
                  disabled={uploadingProof}
                >
                  {uploadingProof ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Submit Payment Details
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Cash Payment Section */}
          {paymentMethod === 'cash' && (
            <div className="bg-green-50 dark:bg-green-950/30 rounded-2xl border border-green-200 dark:border-green-800 p-6">
              <h3 className="font-semibold text-green-700 dark:text-green-300 mb-4 text-center">
                Pay by Cash at Bulaccino Cafe
              </h3>
              
              <div className="text-center space-y-4">
                <div className="bg-white dark:bg-gray-900 rounded-xl p-4">
                  <p className="text-green-700 dark:text-green-300 mb-2">Visit us at:</p>
                  <p className="font-semibold text-foreground">Bulaccino Cafe</p>
                  <p className="text-sm text-muted-foreground">Nadi, Fiji</p>
                </div>
                
                <p className="text-green-600 dark:text-green-400">
                  Bring your deposit of <strong>${newOrderPayment?.deposit_amount?.toFixed(2)}</strong> to secure your order.
                </p>
                
                <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Note:</strong> Your order will be reserved for 48 hours. Please visit the cafe to make your deposit and confirm your booking.
                  </p>
                </div>
                
                <Button
                  onClick={() => {
                    setNewOrderPayment(null);
                    fetchOrders();
                    setView('dashboard');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Got It - Go to Dashboard
                </Button>
              </div>
            </div>
          )}
          
          {/* Card Coming Soon */}
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-2xl border border-blue-200 dark:border-blue-800 p-4 text-center">
            <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              💳 <strong>MasterCard & Visa</strong> payments via BSP & ANZ coming soon!
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  // Login Form
  const renderLogin = () => (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="catering-login">
      <div className="w-full max-w-md">
        <button 
          onClick={() => setView('landing')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        
        <div className="bg-card rounded-2xl border border-border p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Welcome Back</h1>
            <p className="text-muted-foreground">Sign in to your catering account</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Email</label>
              <Input
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 text-center space-y-2">
            <button 
              onClick={() => setView('forgot-password')}
              className="text-sm text-amber-600 hover:underline"
            >
              Forgot password?
            </button>
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{' '}
              <button onClick={() => setView('register')} className="text-amber-600 hover:underline">
                Create one
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  
  // Register Form
  const renderRegister = () => (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="catering-register">
      <div className="max-w-md mx-auto">
        <button 
          onClick={() => setView('landing')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        
        <div className="bg-card rounded-2xl border border-border p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Create Account</h1>
            <p className="text-muted-foreground">Register for corporate catering access</p>
          </div>
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
              <Input
                value={registerForm.full_name}
                onChange={(e) => setRegisterForm({...registerForm, full_name: e.target.value})}
                placeholder="Your full name"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
              <Input
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                placeholder="your@email.com"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Phone *</label>
              <Input
                value={registerForm.phone}
                onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                placeholder="+679 xxx xxxx"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Company Name *</label>
              <Input
                value={registerForm.company_name}
                onChange={(e) => setRegisterForm({...registerForm, company_name: e.target.value})}
                placeholder="Your company"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Position / Role</label>
              <Input
                value={registerForm.position}
                onChange={(e) => setRegisterForm({...registerForm, position: e.target.value})}
                placeholder="e.g., Office Manager"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Password *</label>
              <Input
                type="password"
                value={registerForm.password}
                onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                placeholder="At least 6 characters"
                required
                minLength={6}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Confirm Password *</label>
              <Input
                type="password"
                value={registerForm.confirmPassword}
                onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                placeholder="Confirm your password"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
            </Button>
          </form>
          
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <button onClick={() => setView('login')} className="text-amber-600 hover:underline">
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
  
  // Dashboard
  const renderDashboard = () => (
    <div className="min-h-screen bg-background" data-testid="catering-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-foreground">My Account</h1>
              <p className="text-sm text-muted-foreground">{client?.full_name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setView('menu')}
                variant="outline"
                className="rounded-full"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                New Order
              </Button>
              <Button 
                onClick={handleLogout}
                variant="ghost"
                size="icon"
                className="rounded-full"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => { setActiveTab('orders'); fetchOrders(); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'orders'
                ? 'bg-amber-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <History className="w-4 h-4 inline mr-2" />
            My Orders
          </button>
          <button
            onClick={() => { setActiveTab('templates'); fetchTemplates(); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'templates'
                ? 'bg-amber-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="templates-tab"
          >
            <Bookmark className="w-4 h-4 inline mr-2" />
            Saved Templates
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === 'profile'
                ? 'bg-amber-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Profile
          </button>
        </div>
        
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No orders yet</h3>
                <p className="text-muted-foreground mb-4">Start your first catering order</p>
                <Button onClick={() => setView('menu')} className="rounded-full">
                  Browse Menu
                </Button>
              </div>
            ) : (
              orders.map(order => (
                <div key={order.order_id} className="bg-card rounded-2xl border border-border p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-foreground">#{order.order_id?.slice(-8)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          order.status === 'confirmed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          order.status === 'cancelled' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {order.status?.replace(/_/g, ' ')}
                        </span>
                        {/* Deposit status badge */}
                        {order.deposit_status && order.deposit_status !== 'paid' && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            order.deposit_status === 'payment_proof_submitted' ? 'bg-blue-100 text-blue-700' :
                            order.deposit_status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {order.deposit_status === 'payment_proof_submitted' ? 'Verifying Payment' :
                             order.deposit_status === 'partial' ? 'Partial Payment' : 'Deposit Pending'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {order.event_date} at {order.event_time}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-foreground">${order.gross_total?.toFixed(2)}</div>
                      <div className="text-xs text-muted-foreground">{order.items?.length} items</div>
                    </div>
                  </div>
                  
                  <div className="text-sm text-muted-foreground mb-4">
                    <p><strong>Occasion:</strong> {order.occasion}</p>
                    <p><strong>Guests:</strong> {order.guest_count}</p>
                  </div>
                  
                  {/* Payment section */}
                  {order.deposit_status !== 'paid' && order.status !== 'cancelled' && order.status !== 'completed' && (
                    <div className="pt-4 border-t border-border">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            Deposit Required: <span className="text-amber-600">${order.deposit_required?.toFixed(2)}</span>
                          </div>
                          {order.deposit_amount_paid > 0 && (
                            <div className="text-xs text-green-600">
                              Paid: ${order.deposit_amount_paid?.toFixed(2)}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => openPaymentModal(order)}
                          className="rounded-full bg-green-600 hover:bg-green-700"
                        >
                          <Smartphone className="w-4 h-4 mr-1" />
                          Pay with M-PAiSA
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Save as Template option */}
                  <div className="pt-3 border-t border-border mt-3">
                    <button
                      onClick={() => saveOrderAsTemplate(order)}
                      className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                    >
                      <BookmarkPlus className="w-4 h-4" />
                      Save as reusable template
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        
        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="space-y-4" data-testid="templates-content">
            {/* Save Current Cart as Template Button */}
            {cart.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-2xl border border-amber-200 dark:border-amber-800 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-foreground">Save Current Cart</h4>
                    <p className="text-sm text-muted-foreground">{cart.length} items in cart</p>
                  </div>
                  <Button 
                    onClick={() => setShowSaveTemplateModal(true)}
                    className="rounded-full bg-amber-500 hover:bg-amber-600"
                  >
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                    Save as Template
                  </Button>
                </div>
              </div>
            )}
            
            {loadingTemplates ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 bg-card rounded-2xl border border-border">
                <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No saved templates</h3>
                <p className="text-muted-foreground mb-4">
                  Save your favorite orders as templates for quick reordering
                </p>
                <Button onClick={() => setView('menu')} className="rounded-full">
                  Browse Menu
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {templates.map(template => (
                  <div key={template.template_id} className="bg-card rounded-2xl border border-border p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground">{template.name}</h3>
                        {template.description && (
                          <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteTemplate(template.template_id, template.name)}
                          className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                      <span>{template.item_count} items</span>
                      <span>~${template.estimated_subtotal?.toFixed(2)}</span>
                      {template.default_guest_count && (
                        <span>{template.default_guest_count} guests</span>
                      )}
                      {template.times_used > 0 && (
                        <span className="text-green-600">Used {template.times_used}x</span>
                      )}
                    </div>
                    
                    {/* Template items preview */}
                    <div className="bg-muted/30 rounded-xl p-3 mb-4">
                      <p className="text-xs text-muted-foreground mb-2">Items:</p>
                      <div className="flex flex-wrap gap-2">
                        {template.items?.slice(0, 5).map((item, idx) => (
                          <span key={idx} className="px-2 py-1 bg-background rounded-full text-xs text-foreground">
                            {item.quantity}x {item.name}
                          </span>
                        ))}
                        {template.items?.length > 5 && (
                          <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full text-xs text-amber-700 dark:text-amber-400">
                            +{template.items.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => loadTemplateToCart(template)}
                      className="w-full rounded-full bg-amber-500 hover:bg-amber-600"
                      data-testid={`use-template-${template.template_id}`}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Use This Template
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'profile' && (
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4">Profile Information</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span className="text-foreground">{client?.full_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="text-foreground">{client?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone</span>
                <span className="text-foreground">{client?.phone}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Company</span>
                <span className="text-foreground">{client?.company_name}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  // Main render
  return (
    <div className="min-h-screen bg-background text-foreground">
      {view === 'landing' && renderLanding()}
      {view === 'menu' && renderMenu()}
      {view === 'cart' && renderCart()}
      {view === 'checkout' && renderCheckout()}
      {view === 'payment' && renderPayment()}
      {view === 'login' && renderLogin()}
      {view === 'register' && renderRegister()}
      {view === 'forgot-password' && renderLogin()} {/* Reuse login for now */}
      {view === 'dashboard' && renderDashboard()}
      
      {/* M-PAiSA Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowPaymentModal(null)}>
          <div className="bg-card rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Pay with M-PAiSA</h2>
                  <p className="text-sm text-muted-foreground">Order #{showPaymentModal.order_id?.slice(-8)}</p>
                </div>
                <button onClick={() => setShowPaymentModal(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Payment Method Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setPaymentMethod?.('mpaisa')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    (!paymentMethod || paymentMethod === 'mpaisa') 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  M-PAiSA
                </button>
                <button
                  onClick={() => setPaymentMethod?.('bank')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    paymentMethod === 'bank' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Bank Transfer
                </button>
                <button
                  onClick={() => setPaymentMethod?.('cash')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    paymentMethod === 'cash' 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Cash (In-Store)
                </button>
              </div>

              {/* M-PAiSA Instructions */}
              {(!paymentMethod || paymentMethod === 'mpaisa') && (
              <div className="bg-green-50 dark:bg-green-950/30 rounded-xl p-4 border border-green-200 dark:border-green-800">
                <h3 className="font-semibold text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  M-PAiSA Payment
                </h3>
                {mpaisaInfo ? (
                  <div className="space-y-3">
                    <p className="text-sm text-green-700 dark:text-green-400"><strong>Merchant:</strong> {mpaisaInfo.merchant_name}</p>
                    
                    {/* QR Code Display */}
                    {mpaisaInfo.qr_code_url && (
                      <div className="flex justify-center my-4">
                        <div className="bg-white p-3 rounded-xl shadow-lg">
                          <img 
                            src={mpaisaInfo.qr_code_url} 
                            alt="M-PAiSA QR Code" 
                            className="w-48 h-48 object-contain"
                          />
                        </div>
                      </div>
                    )}
                    
                    <ol className="list-decimal list-inside space-y-1 text-sm text-green-700 dark:text-green-400">
                      {mpaisaInfo.instructions?.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ol>
                  </div>
                ) : (
                  <p className="text-sm">Loading payment info...</p>
                )}
              </div>
              )}

              {/* Bank Transfer Instructions */}
              {paymentMethod === 'bank' && mpaisaInfo?.bank_transfer && (
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Bank Transfer Details
                </h3>
                <div className="space-y-2 text-sm text-blue-700 dark:text-blue-400">
                  <p><strong>Bank:</strong> {mpaisaInfo.bank_transfer.bank_name}</p>
                  <p><strong>Account Name:</strong> {mpaisaInfo.bank_transfer.account_name}</p>
                  <p><strong>Account Number:</strong> <span className="font-mono text-lg font-bold">{mpaisaInfo.bank_transfer.account_number}</span></p>
                  <p className="text-xs mt-2 text-blue-600 dark:text-blue-500">Use Order ID <strong>{showPaymentModal.order_id}</strong> as reference</p>
                  <ol className="list-decimal list-inside space-y-1 mt-3">
                    {mpaisaInfo.bank_transfer.instructions?.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
              )}

              {/* Cash Payment Instructions */}
              {paymentMethod === 'cash' && (
              <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Cash Payment (In-Store)
                </h3>
                <div className="space-y-2 text-sm text-amber-700 dark:text-amber-400">
                  <p>Visit us at <strong>Bulaccino Cafe Namaka</strong> to make your deposit in person.</p>
                  <p className="mt-2">Your order will remain in <strong>pending</strong> status until payment is received.</p>
                  <p className="mt-2">Please bring your <strong>Order ID: {showPaymentModal.order_id}</strong></p>
                  <div className="mt-3 p-3 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                    <p className="font-medium">Location:</p>
                    <p>Bulaccino Cafe Namaka, Queens Road, Nadi</p>
                  </div>
                </div>
              </div>
              )}
              
              {/* Amount to Pay */}
              <div className="text-center py-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Deposit Amount to Pay</p>
                <p className="text-3xl font-bold text-amber-600">${showPaymentModal.deposit_required?.toFixed(2)}</p>
              </div>
              
              {/* Payment Proof Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Transaction Reference * <span className="text-xs text-muted-foreground">(from M-PAiSA confirmation)</span>
                  </label>
                  <Input
                    value={paymentProofForm.transaction_ref}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, transaction_ref: e.target.value})}
                    placeholder="e.g., MP123456789"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your M-PAiSA Phone Number *
                  </label>
                  <Input
                    value={paymentProofForm.payer_phone}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, payer_phone: e.target.value})}
                    placeholder="+679 xxx xxxx"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Amount Paid (FJD) *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentProofForm.amount}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, amount: e.target.value})}
                    placeholder={showPaymentModal.deposit_required?.toFixed(2)}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Upload Payment Screenshot *
                  </label>
                  <div className="border-2 border-dashed border-border rounded-xl p-4 text-center hover:border-amber-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="proof-upload"
                    />
                    <label htmlFor="proof-upload" className="cursor-pointer">
                      {proofFile ? (
                        <div className="flex items-center justify-center gap-2 text-green-600">
                          <ImageIcon className="w-5 h-5" />
                          <span>{proofFile.name}</span>
                        </div>
                      ) : (
                        <div className="text-muted-foreground">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Click to upload screenshot</p>
                          <p className="text-xs">JPEG, PNG, WebP (max 5MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes (optional)
                  </label>
                  <Input
                    value={paymentProofForm.notes}
                    onChange={(e) => setPaymentProofForm({...paymentProofForm, notes: e.target.value})}
                    placeholder="Any additional notes"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-border flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPaymentModal(null)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={uploadPaymentProof}
                disabled={uploadingProof || !proofFile}
                className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
              >
                {uploadingProof ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit Payment Proof
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSaveTemplateModal(false)}>
          <div className="bg-card rounded-2xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-border">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-foreground">Save as Template</h2>
                  <p className="text-sm text-muted-foreground">{cart.length} items</p>
                </div>
                <button onClick={() => setShowSaveTemplateModal(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Template Name *
                </label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  placeholder="e.g., Monthly Meeting Order"
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description (optional)
                </label>
                <Textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                  placeholder="Brief description of this template"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Default Guest Count
                  </label>
                  <Input
                    type="number"
                    value={templateForm.default_guest_count}
                    onChange={(e) => setTemplateForm({...templateForm, default_guest_count: e.target.value})}
                    placeholder="e.g., 20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Default Occasion
                  </label>
                  <select
                    value={templateForm.default_occasion}
                    onChange={(e) => setTemplateForm({...templateForm, default_occasion: e.target.value})}
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground"
                  >
                    <option value="">Select</option>
                    {OCCASIONS.map(occ => (
                      <option key={occ.value} value={occ.value}>{occ.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Notes (optional)
                </label>
                <Textarea
                  value={templateForm.notes}
                  onChange={(e) => setTemplateForm({...templateForm, notes: e.target.value})}
                  placeholder="Any special notes for this template"
                  rows={2}
                />
              </div>
              
              {/* Preview items */}
              <div className="bg-muted/30 rounded-xl p-3">
                <p className="text-xs text-muted-foreground mb-2">Items to save:</p>
                <div className="flex flex-wrap gap-2">
                  {cart.slice(0, 4).map((item, idx) => (
                    <span key={idx} className="px-2 py-1 bg-background rounded-full text-xs text-foreground">
                      {item.quantity}x {item.name}
                    </span>
                  ))}
                  {cart.length > 4 && (
                    <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full text-xs text-amber-700 dark:text-amber-400">
                      +{cart.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-border flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSaveTemplateModal(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCartAsTemplate}
                disabled={loading || !templateForm.name.trim()}
                className="flex-1 rounded-full bg-amber-500 hover:bg-amber-600"
                data-testid="save-template-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <BookmarkPlus className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Menu Item Card Component
function MenuItemCard({ item, onAddToCart, cartItem }) {
  const [quantity, setQuantity] = useState(item.min_qty || 1);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState('');
  
  // Placeholder images by category
  const CATEGORY_PLACEHOLDERS = {
    sweets: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=200&h=200&fit=crop',
    hot_snacks: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?w=200&h=200&fit=crop',
    quiches: 'https://images.unsplash.com/photo-1571748982800-fa51082c2224?w=200&h=200&fit=crop',
    salads: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=200&h=200&fit=crop',
    sandwiches: 'https://images.unsplash.com/photo-1553909489-cd47e0907980?w=200&h=200&fit=crop',
    wraps: 'https://images.unsplash.com/photo-1626700051175-6818013e1d4f?w=200&h=200&fit=crop',
    deli_pies: 'https://images.unsplash.com/photo-1621743478914-cc8a86d7e7b5?w=200&h=200&fit=crop',
    cakes: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=200&h=200&fit=crop',
    beverages: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=200&h=200&fit=crop',
    extras: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=200&fit=crop',
  };
  
  const imageUrl = item.image || CATEGORY_PLACEHOLDERS[item.category] || CATEGORY_PLACEHOLDERS.extras;
  const hasRealImage = !!item.image;
  const hasOptions = item.special_instructions_options?.length > 0;
  
  const handleAddToCart = () => {
    const itemWithInstructions = {
      ...item,
      special_instruction: selectedInstruction || null
    };
    onAddToCart(itemWithInstructions, quantity);
    setQuantity(item.min_qty || 1);
    setSelectedInstruction('');
  };
  
  return (
    <>
      <div className="bg-card rounded-xl border border-border overflow-hidden hover:border-amber-300 transition-colors">
        {/* Image Thumbnail - Consistent aspect ratio container */}
        <div 
          className="relative aspect-[4/3] bg-muted cursor-pointer group overflow-hidden"
          onClick={() => setShowImageModal(true)}
        >
          <img 
            src={imageUrl}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.target.src = CATEGORY_PLACEHOLDERS[item.category] || CATEGORY_PLACEHOLDERS.extras;
            }}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {!hasRealImage && (
            <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/50 rounded text-[10px] text-white/70">
              Stock photo
            </div>
          )}
        </div>
        
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-medium text-foreground leading-tight">{item.name}</h3>
            <div className="text-right">
              <div className="font-semibold text-foreground">${item.price?.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground">/ {item.unit}</div>
            </div>
          </div>
          
          {item.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
          )}
          
          {/* Dietary tags */}
          {item.dietary_tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {item.dietary_tags.map(tag => (
                <span 
                  key={tag}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${DIETARY_COLORS[tag] || 'bg-gray-100 text-gray-600'}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          
          {/* Serves info */}
          {item.serves && (
            <p className="text-xs text-muted-foreground mb-3">
              Serves: {item.serves}
            </p>
          )}
          
          {/* Special Instructions Selector */}
          {hasOptions && (
            <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                {item.special_instructions || 'Select option'}
              </label>
              <select
                value={selectedInstruction}
                onChange={(e) => setSelectedInstruction(e.target.value)}
                className="w-full text-sm rounded border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-800 px-2 py-1.5"
              >
                <option value="">Choose...</option>
                {item.special_instructions_options.map((opt, idx) => (
                  <option key={idx} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Add to cart */}
          <div className="flex items-center justify-between mt-auto pt-2 border-t border-border">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setQuantity(Math.max(item.min_qty || 1, quantity - 1))}
                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-8 text-center text-sm font-medium">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-7 h-7 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
            
            <Button
              size="sm"
              onClick={handleAddToCart}
              disabled={hasOptions && !selectedInstruction}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
              data-testid={`add-to-cart-${item.item_id}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          
          {item.min_qty > 1 && (
            <p className="text-xs text-muted-foreground mt-2">
              Min. order: {item.min_qty}
            </p>
          )}
        </div>
      </div>
      
      {/* Image Enlargement Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-2xl w-full">
            <button 
              className="absolute -top-10 right-0 text-white hover:text-amber-400"
              onClick={() => setShowImageModal(false)}
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={imageUrl?.replace('w=200&h=200', 'w=800&h=600')}
              alt={item.name}
              className="w-full rounded-xl shadow-2xl"
            />
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent rounded-b-xl">
              <h3 className="text-xl font-bold text-white">{item.name}</h3>
              <p className="text-white/80">${item.price?.toFixed(2)} / {item.unit}</p>
              {item.description && (
                <p className="text-white/60 text-sm mt-1">{item.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
